import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import ClientAddSheet from "./ClientAddSheet";
import ClientAddMethodSheet from "./ClientAddMethodSheet";
import ClientListCard from "./ClientListCard";
import RegistrationModerationSheet from "./RegistrationModerationSheet";
import { PAGE_SIZE } from "./patientConstants";
import { usePatientsBase } from "../../../utils/hubPaths";
import {
  promptPortalInviteCopy,
  showPortalInviteFeedback,
} from "../../../utils/portalInviteFeedback";
import aa from "../AdminPortal/adminApp.module.css";
import SwipeableRow from "../AdminPortal/SwipeableRow";
import { HubNavAdd, HubSectionTitle, HubTabScreen } from "../AdminPortal/HubPwa";
import L from "../../../constants/baysportLabels";
import s from "../clinic.module.css";
import { useAdminAppMode } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import md from "../common/manageDesktop.module.css";
import {
  Button,
  Chip,
  ChipGroup,
  EmptyState,
  Input,
  useToast,
  IconPlus,
  IconSearch,
  IconUsers,
} from "../../ui";

const FILTERS = [
  { id: "all", label: "Все" },
  { id: "active", label: "Активные" },
  { id: "pending", label: "На модерации" },
  { id: "debtors", label: "Должники" },
  { id: "no_membership", label: "Без абон." },
  { id: "archived", label: "Архив" },
];

const AUDIENCE_TABS = [
  { id: "athletes", label: "Ученики" },
  { id: "guardians", label: "Родители" },
];

function clientCountLabel(n, filter) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = "клиентов";
  if (mod10 === 1 && mod100 !== 11) word = "клиент";
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    word = "клиента";
  }
  if (filter === "archived") return `${n} в архиве`;
  return `${n} ${word}`;
}

function buildClientSections(clients) {
  const byGroup = new Map();
  const ungrouped = [];

  for (const client of clients) {
    const primary = client.sport?.groups?.[0];
    if (!primary) {
      ungrouped.push(client);
      continue;
    }
    if (!byGroup.has(primary)) byGroup.set(primary, []);
    byGroup.get(primary).push(client);
  }

  const sections = [...byGroup.entries()]
    .sort(([a], [b]) => a.localeCompare(b, "ru"))
    .map(([title, list]) => ({ id: title, title, clients: list }));

  if (ungrouped.length) {
    sections.push({
      id: "__none",
      title: "Без группы",
      clients: ungrouped,
    });
  }

  return sections;
}

export default function AdminPatientsList({ staffMode = false }) {
  const patientsBase = usePatientsBase();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const isAdminApp = useAdminAppMode();
  const isMobile = useIsMobile(768);
  const isApp = isAdminApp && isMobile;
  const isDesktop = isAdminApp && !isMobile;
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filter, setFilter] = useState(staffMode ? "active" : "all");
  const [audience, setAudience] = useState("athletes");
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState(
    () => searchParams.get("group") || "",
  );
  const [methodOpen, setMethodOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [moderationTarget, setModerationTarget] = useState(null);
  const [moderationBusy, setModerationBusy] = useState(false);

  useEffect(() => {
    if (searchParams.get("add") === "1") {
      setMethodOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
    if (searchParams.get("registration") === "pending") {
      setFilter("pending");
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    clinicAPI
      .getSportGroupsSummary()
      .then((r) => setGroups(r.groups || []))
      .catch(() => setGroups([]));
  }, []);

  const setGroupFilterAndUrl = (id) => {
    setGroupFilter(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set("group", id);
    else next.delete("group");
    setSearchParams(next, { replace: true });
  };

  const loadList = useCallback(
    async (append = false, startOffset = 0) => {
      setLoading(!append);
      try {
        const r = await clinicAPI.getSportClients({
          q: debouncedQ || undefined,
          filter,
          audience,
          group_id: groupFilter || undefined,
          limit: PAGE_SIZE,
          offset: startOffset,
        });
        const list = r.clients || [];
        setClients(append ? (prev) => [...prev, ...list] : list);
        setTotal(r.total ?? list.length);
        setOffset(startOffset);
      } catch (e) {
        toast.error(e.response?.data?.error || "Ошибка загрузки");
      } finally {
        setLoading(false);
      }
    },
    [debouncedQ, filter, audience, groupFilter, toast],
  );

  useEffect(() => {
    loadList(false, 0);
  }, [debouncedQ, filter, audience, groupFilter, loadList]);

  useAdminRefresh(() => {
    loadList(false, 0);
  });

  const archiveClient = async (client) => {
    if (client.sport?.is_archived) return;
    if (!window.confirm(`Отправить «${client.full_name}» в архив?`)) return;
    try {
      await clinicAPI.updatePatient(client.id, {
        is_archived: true,
      });
      toast.success("Клиент в архиве");
      loadList(false, 0);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось архивировать");
    }
  };

  const softDeleteClient = async (client) => {
    const name = client.full_name || "клиента";
    if (
      !window.confirm(
        `Удалить «${name}» из списка клиентов?\n\nКлиент исчезнет из раздела «Клиенты», но история оплат и посещений сохранится для статистики.`,
      )
    ) {
      return;
    }
    try {
      await clinicAPI.softDeleteClient(client.id);
      toast.success("Клиент удалён из списка");
      loadList(false, 0);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось удалить");
    }
  };

  const restoreClient = async (client) => {
    if (!client.sport?.is_archived) return;
    try {
      await clinicAPI.updatePatient(client.id, {
        is_archived: false,
      });
      toast.success("Клиент восстановлен");
      loadList(false, 0);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось восстановить");
    }
  };

  const openModeration = (client, status) => {
    setModerationTarget({ client, status });
  };

  const confirmModeration = async () => {
    if (!moderationTarget || moderationBusy) return;
    const { client, status } = moderationTarget;
    setModerationBusy(true);
    try {
      const r = await clinicAPI.moderateClientRegistration(client.id, status);
      if (status === "approved") {
        const { needsManualCopy, inviteUrl } = await showPortalInviteFeedback(
          toast,
          r,
          "Регистрация подтверждена",
        );
        if (needsManualCopy) promptPortalInviteCopy(inviteUrl);
      } else {
        toast.success(r.message || "Заявка отклонена");
      }
      setModerationTarget(null);
      loadList(false, 0);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось обработать заявку");
    } finally {
      setModerationBusy(false);
    }
  };

  const hasMore = clients.length < total;
  const groupBySection =
    !groupFilter &&
    audience === "athletes" &&
    !debouncedQ &&
    (filter === "active" || filter === "pending" || filter === "all");
  const clientSections = groupBySection ? buildClientSections(clients) : null;

  const renderClientList = (list) => (
    <ul className={isDesktop ? s.clientsList : `${s.clientsList} ${aa.clientsList}`}>
      {list.map((c) => {
        const archived = c.sport?.is_archived;
        const pendingReg = c.sport?.registration_status === "pending";
        const swipeActions =
          isApp && !staffMode
            ? pendingReg
              ? [
                  {
                    id: "approve",
                    label: "Одобрить",
                    tone: "default",
                    onClick: () => openModeration(c, "approved"),
                  },
                  {
                    id: "reject",
                    label: "Отклонить",
                    tone: "danger",
                    onClick: () => openModeration(c, "rejected"),
                  },
                ]
              : archived
                ? [
                    {
                      id: "restore",
                      label: "Вернуть",
                      tone: "default",
                      onClick: () => restoreClient(c),
                    },
                    {
                      id: "delete",
                      label: "Удалить",
                      tone: "danger",
                      onClick: () => softDeleteClient(c),
                    },
                  ]
                : [
                    {
                      id: "archive",
                      label: "Архив",
                      tone: "default",
                      onClick: () => archiveClient(c),
                    },
                    {
                      id: "delete",
                      label: "Удалить",
                      tone: "danger",
                      onClick: () => softDeleteClient(c),
                    },
                  ]
            : [];

        const card = (
          <ClientListCard
            client={c}
            variant={isDesktop ? "desktop" : isApp ? "app" : "default"}
          />
        );

        return (
          <li key={c.id}>
            {swipeActions.length ? (
              <SwipeableRow actions={swipeActions}>{card}</SwipeableRow>
            ) : (
              card
            )}
          </li>
        );
      })}
    </ul>
  );

  const emptyTitle =
    audience === "guardians"
      ? debouncedQ
        ? "Родителей не нашли"
        : "Пока нет родителей в списке"
      : filter === "pending"
      ? debouncedQ
        ? "Заявок не нашли"
        : "Нет заявок на модерации"
      : filter === "archived"
      ? debouncedQ
        ? "В архиве никого не нашли"
        : "Архив пуст"
      : debouncedQ
        ? "Никого не нашли"
        : "Пока нет клиентов";

  const emptyDescription = staffMode
    ? debouncedQ
      ? "Попробуйте другой запрос"
      : "В ваших группах пока нет спортсменов"
    : filter === "archived"
      ? "Здесь появятся клиенты после архивации из списка активных"
      : debouncedQ
        ? "Попробуйте другой запрос или сбросьте фильтр"
        : "Добавьте первого спортсмена в клуб";

  const openAddClient = () => {
    setMethodOpen(true);
  };

  const visibleFilters = staffMode
    ? FILTERS.filter(
        (f) => !["debtors", "pending", "archived", "no_membership"].includes(f.id),
      )
    : FILTERS;

  const modals = (
    <>
      {!staffMode ? (
        <>
          <ClientAddMethodSheet
            open={methodOpen}
            onClose={() => setMethodOpen(false)}
            onManual={() => setAddOpen(true)}
          />
          <ClientAddSheet
            open={addOpen}
            onClose={() => setAddOpen(false)}
            onCreated={() => loadList(false, 0)}
          />
        </>
      ) : null}
      <RegistrationModerationSheet
        open={Boolean(moderationTarget)}
        client={moderationTarget?.client}
        status={moderationTarget?.status}
        busy={moderationBusy}
        onClose={() => !moderationBusy && setModerationTarget(null)}
        onConfirm={confirmModeration}
      />
    </>
  );

  const listContent =
    clients.length === 0 && !loading ? (
      <EmptyState
        icon={IconUsers}
        title={emptyTitle}
        description={emptyDescription}
        actionLabel={
          staffMode || debouncedQ || filter === "archived"
            ? undefined
            : "Добавить клиента"
        }
        onAction={
          staffMode || debouncedQ || filter === "archived"
            ? undefined
            : openAddClient
        }
      />
    ) : clientSections ? (
      <div
        className={
          isDesktop ? md.desktopClientsByGroupWrap : aa.clientsByGroupWrap
        }
      >
        {clientSections.map((section) => (
          <section
            key={section.id}
            className={
              isDesktop
                ? md.desktopClientsByGroupSection
                : aa.clientsByGroupSection
            }
          >
            {isDesktop ? (
              <h3 className={md.desktopClientsByGroupTitle}>{section.title}</h3>
            ) : (
              <HubSectionTitle>{section.title}</HubSectionTitle>
            )}
            <div
              className={
                isDesktop ? md.desktopClientsList : aa.hubCardList
              }
            >
              {renderClientList(section.clients)}
            </div>
          </section>
        ))}
      </div>
    ) : (
      <div className={isDesktop ? md.desktopClientsList : aa.hubCardList}>
        {renderClientList(clients)}
      </div>
    );

  if (isDesktop) {
    return (
      <div className={md.desktopClientsPage}>
        {!staffMode ? (
          <Link
            to={`${patientsBase}/registration`}
            className={md.desktopClientsRegLink}
          >
            <span className={md.desktopClientsRegIcon} aria-hidden>
              🔗
            </span>
            <span className={md.desktopClientsRegText}>
              <strong>Ссылки регистрации</strong>
              <span>Публичные ссылки и QR для набора клиентов</span>
            </span>
            <span className={md.desktopClientsRegChevron} aria-hidden />
          </Link>
        ) : null}

        <div className={md.desktopClientsPanel}>
          <div className={md.desktopClientsToolbar}>
            <label className={md.desktopClientsSearch}>
              <IconSearch className={md.desktopClientsSearchIcon} width={18} height={18} />
              <Input
                className={md.desktopClientsSearchInput}
                placeholder="ФИО, телефон или email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Поиск клиентов"
              />
            </label>
            {!staffMode ? (
              <div className={md.desktopClientsToolbarActions}>
                <Button type="button" onClick={openAddClient}>
                  <IconPlus width={16} height={16} />
                  Добавить
                </Button>
              </div>
            ) : null}
          </div>

          <div className={md.desktopClientsFilters}>
            <ChipGroup label="Раздел">
              {AUDIENCE_TABS.map((t) => (
                <Chip
                  key={t.id}
                  selected={audience === t.id}
                  onClick={() => setAudience(t.id)}
                >
                  {t.label}
                </Chip>
              ))}
            </ChipGroup>
            <ChipGroup label="Фильтр клиентов">
              {visibleFilters.map((f) => (
                <Chip
                  key={f.id}
                  selected={filter === f.id}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}
                </Chip>
              ))}
            </ChipGroup>
            {groups.length > 0 ? (
              <ChipGroup label="Группа">
                <Chip
                  selected={!groupFilter}
                  onClick={() => setGroupFilterAndUrl("")}
                >
                  Все группы
                </Chip>
                {groups.map((g) => (
                  <Chip
                    key={g.id}
                    selected={groupFilter === String(g.id)}
                    onClick={() => setGroupFilterAndUrl(String(g.id))}
                  >
                    {g.name}
                  </Chip>
                ))}
              </ChipGroup>
            ) : null}
          </div>

          {!loading && clients.length > 0 ? (
            <p className={md.desktopClientsMeta}>{clientCountLabel(total, filter)}</p>
          ) : null}

          {listContent}

          {hasMore ? (
            <button
              type="button"
              className={md.desktopClientsLoadMore}
              onClick={() => loadList(true, offset + PAGE_SIZE)}
              disabled={loading}
            >
              {loading ? "Загрузка…" : `Показать ещё · ${clients.length} из ${total}`}
            </button>
          ) : null}
        </div>

        {modals}
      </div>
    );
  }

  const listBody = (
    <>
      {!staffMode ? (
        <Link
          to={`${patientsBase}/registration`}
          className={aa.clientsRegistrationBanner}
        >
          <span className={aa.clientsRegistrationBannerIcon} aria-hidden>
            🔗
          </span>
          <span className={aa.clientsRegistrationBannerBody}>
            <strong>Ссылки регистрации</strong>
            <span>Публичные ссылки и QR для набора клиентов</span>
          </span>
          <span className={aa.clientsRegistrationBannerChevron} aria-hidden />
        </Link>
      ) : null}

      <div className={aa.clientsHubTop}>
        <div className={aa.clientsSearchRow}>
          <label className={aa.clientsSearch}>
            <IconSearch className={aa.clientsSearchIcon} width={18} height={18} />
            <Input
              className={aa.clientsSearchInput}
              placeholder="ФИО, телефон или email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск клиентов"
            />
          </label>
          {!isApp && !staffMode ? (
            <button
              type="button"
              className={aa.clientsAddBtn}
              onClick={openAddClient}
              aria-label="Добавить клиента"
              title="Добавить клиента"
            >
              <IconPlus width={20} height={20} />
            </button>
          ) : null}
        </div>

        <div className={aa.clientsFilterSection}>
          <p className={aa.clientsFilterLabel}>Раздел</p>
          <ChipGroup label="Раздел клиентов">
            {AUDIENCE_TABS.map((t) => (
              <Chip
                key={t.id}
                selected={audience === t.id}
                onClick={() => setAudience(t.id)}
              >
                {t.label}
              </Chip>
            ))}
          </ChipGroup>
          <p className={aa.clientsFilterLabel}>Показать</p>
          <ChipGroup label="Фильтр клиентов">
            {visibleFilters.map((f) => (
              <Chip
                key={f.id}
                selected={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </Chip>
            ))}
          </ChipGroup>
          {groups.length > 0 ? (
            <>
              <p className={aa.clientsFilterLabel}>Группа</p>
              <ChipGroup label="Группа">
                <Chip
                  selected={!groupFilter}
                  onClick={() => setGroupFilterAndUrl("")}
                >
                  Все группы
                </Chip>
                {groups.map((g) => (
                  <Chip
                    key={g.id}
                    selected={groupFilter === String(g.id)}
                    onClick={() => setGroupFilterAndUrl(String(g.id))}
                  >
                    {g.name}
                  </Chip>
                ))}
              </ChipGroup>
            </>
          ) : null}
        </div>

        {!loading ? (
          <p className={aa.clientsCount}>{clientCountLabel(total, filter)}</p>
        ) : null}
      </div>

      {clients.length === 0 && !loading ? (
        <EmptyState
          icon={IconUsers}
          title={emptyTitle}
          description={emptyDescription}
          actionLabel={
            staffMode || debouncedQ || filter === "archived"
              ? undefined
              : "Добавить клиента"
          }
          onAction={
            staffMode || debouncedQ || filter === "archived"
              ? undefined
              : openAddClient
          }
        />
      ) : clientSections ? (
        <div className={aa.clientsByGroupWrap}>
          {clientSections.map((section) => (
            <section key={section.id} className={aa.clientsByGroupSection}>
              <HubSectionTitle>{section.title}</HubSectionTitle>
              <div className={aa.hubCardList}>
                {renderClientList(section.clients)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className={aa.hubCardList}>{renderClientList(clients)}</div>
      )}

      {hasMore ? (
        <button
          type="button"
          className={aa.clientsLoadMore}
          onClick={() => loadList(true, offset + PAGE_SIZE)}
          disabled={loading}
        >
          {loading ? "Загрузка…" : `Показать ещё · ${clients.length} из ${total}`}
        </button>
      ) : null}

    </>
  );

  if (isApp) {
    return (
      <>
        <HubTabScreen
          title={L.clients}
          right={
            !staffMode ? (
              <HubNavAdd onClick={openAddClient} ariaLabel="Добавить клиента" />
            ) : null
          }
        >
          {listBody}
        </HubTabScreen>
        {modals}
      </>
    );
  }

  return (
    <div className={aa.clientsHub}>
      {listBody}
      {modals}
    </div>
  );
}
